// TODO: The publicly exposed parts of this should be in lib/BootstrapUtils.

import invariant from "invariant";
import PropTypes from "prop-types";
import { SIZE_MAP } from "./StyleConfig";

function curry(fn: any) {
  return (...args: any[]) => {
    const last = args[args.length - 1];
    if (typeof last === "function") {
      return fn(...args);
    }
    return (Component: any) => fn(...args, Component);
  };
}

export function prefix(props: any, variant: any) {
  invariant(
    props.bsClass != null,
    "A `bsClass` prop is required for this component",
  );
  return props.bsClass + (variant ? `-${variant}` : "");
}

export const bsClass = curry((defaultClass: any, Component: any) => {
  const propTypes = Component.propTypes || (Component.propTypes = {});
  const defaultProps = Component.defaultProps || (Component.defaultProps = {});

  propTypes.bsClass = PropTypes.string;
  defaultProps.bsClass = defaultClass;

  return Component;
});

export const bsStyles = curry((styles: any, defaultStyle: any, Component: any) => {
  if (typeof defaultStyle !== "string") {
    Component = defaultStyle;
    defaultStyle = undefined;
  }

  const existing = Component.STYLES || [];
  const propTypes = Component.propTypes || {};

  styles.forEach((style: any) => {
    if (existing.indexOf(style) === -1) {
      existing.push(style);
    }
  });

  const propType = PropTypes.oneOf(existing);

  // expose the values on the propType function for documentation
  Component.STYLES = existing;

  propType._values = existing;

  Component.propTypes = {
    ...propTypes,
    bsStyle: propType,
  };

  if (defaultStyle !== undefined) {
    const defaultProps = Component.defaultProps || (Component.defaultProps = {});
    defaultProps.bsStyle = defaultStyle;
  }

  return Component;
});

export const bsSizes = curry((sizes: any, defaultSize: any, Component: any) => {
  if (typeof defaultSize !== "string") {
    Component = defaultSize;
    defaultSize = undefined;
  }

  const existing = Component.SIZES || [];
  const propTypes = Component.propTypes || {};

  sizes.forEach((size: any) => {
    if (existing.indexOf(size) === -1) {
      existing.push(size);
    }
  });

  const values: any = [];
  existing.forEach((size: any) => {
    const mappedSize = SIZE_MAP[size];
    if (mappedSize && mappedSize !== size) {
      values.push(mappedSize);
    }

    values.push(size);
  });

  const propType = PropTypes.oneOf(values);

  propType._values = values;

  // expose the values on the propType function for documentation
  Component.SIZES = existing;

  Component.propTypes = {
    ...propTypes,
    bsSize: propType,
  };

  if (defaultSize !== undefined) {
    if (!Component.defaultProps) {
      Component.defaultProps = {};
    }
    Component.defaultProps.bsSize = defaultSize;
  }

  return Component;
});

export function getClassSet(props: any) {
  const classes = {

    [prefix(props)]: true,
  };

  if (props.bsSize) {
    const bsSize = SIZE_MAP[props.bsSize] || props.bsSize;
    classes[prefix(props, bsSize)] = true;
  }

  if (props.bsStyle) {
    classes[prefix(props, props.bsStyle)] = true;
  }

  return classes;
}

function getBsProps(props: any) {
  return {
    bsClass: props.bsClass,
    bsSize: props.bsSize,
    bsStyle: props.bsStyle,
    bsRole: props.bsRole,
  };
}

function isBsProp(propName: any) {
  return (
    propName === "bsClass"
    || propName === "bsSize"
    || propName === "bsStyle"
    || propName === "bsRole"
  );
}

export function splitBsProps(props: any) {
  const elementProps = {};
  Object.entries(props).forEach(([propName, propValue]) => {
    if (!isBsProp(propName)) {
      elementProps[propName] = propValue;
    }
  });

  return [getBsProps(props), elementProps];
}

export function splitBsPropsAndOmit(props: any, omittedPropNames: any) {
  const isOmittedProp = {};
  omittedPropNames.forEach((propName: any) => { isOmittedProp[propName] = true; });

  const elementProps = {};
  Object.entries(props).forEach(([propName, propValue]) => {
    if (!isBsProp(propName) && !isOmittedProp[propName]) {
      elementProps[propName] = propValue;
    }
  });

  return [getBsProps(props), elementProps];
}

/**
 * Add a style variant to a Component. Mutates the propTypes of the component
 * in order to validate the new variant.
 */
export function addStyle(Component: any, ...styleVariant: any[]) {
  bsStyles(styleVariant, Component);
}

export const _curry = curry;
